import { Popover as RadixPopover } from "radix-ui";
import type { ReactNode } from "react";

export type PopoverProps = {
  open: boolean;
  onClose: () => void;
  /** The view the popover attaches to (rendered in place). */
  anchor: ReactNode;
  side?: "top" | "bottom";
  align?: "start" | "center" | "end";
  /** The anchor fills its container (a table cell, a property row) instead of hugging its content. */
  fill?: boolean;
  /** "picker": a fixed-width (300) panel whose scroll views cap at 320 points (option / people / page pickers);
   *  "anchor": as wide as the anchor (suggestion menus over a composer field); "wide": 680 points (editors with a
   *  side list, such as the formula editor); "card": 320 points (a person's contact card). */
  width?: "picker" | "anchor" | "wide" | "card";
  /** Move focus into the popover when it opens (default true). false keeps the keyboard where it is: menus that
   *  follow typing (the editor's slash and @ menus) are driven from the text field. */
  autoFocus?: boolean;
  children?: ReactNode;
};

export function Popover({ open, onClose, anchor, side = "top", align = "end", fill, width, autoFocus = true, children }: PopoverProps) {
  return (
    <RadixPopover.Root open={open} onOpenChange={(o) => (o ? undefined : onClose())}>
      <RadixPopover.Anchor asChild>
        <span className={fill === true ? "b-popover-anchor b-popover-anchor-fill" : "b-popover-anchor"}>{anchor}</span>
      </RadixPopover.Anchor>
      <RadixPopover.Portal>
        <RadixPopover.Content className={width === "picker" ? "b-popover b-popover-picker" : width === "anchor" ? "b-popover b-popover-anchorwidth" : width === "card" ? "b-popover b-popover-card" : width === "wide" ? "b-popover b-popover-wide" : "b-popover"} side={side} align={align} sideOffset={8}
          collisionPadding={12}
          onOpenAutoFocus={autoFocus ? undefined : (e) => e.preventDefault()}
          onCloseAutoFocus={autoFocus ? undefined : (e) => e.preventDefault()}
          // A field inside with its suggestion list open (TextField `suggestionsOpen`) takes Escape to close the list.
          onEscapeKeyDown={(e) => {
            const target = e.target instanceof Element ? e.target : null;
            if (target?.getAttribute("aria-expanded") === "true" && target.getAttribute("aria-autocomplete") === "list") e.preventDefault();
          }}
        >
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
